import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  FaBootstrap,
  FaBrain,
  FaCss3Alt,
  FaDatabase,
  FaGitAlt,
  FaGithub,
  FaHtml5,
  FaJava,
  FaJs,
  FaMapMarkedAlt,
  FaNode,
  FaPython,
  FaReact,
  FaRobot,
} from 'react-icons/fa';
import {
  SiExpo,
  SiExpress,
  SiFlask,
  SiJsonwebtokens,
  SiMongodb,
  SiMysql,
  SiNextdotjs,
  SiPostman,
  SiRedux,
  SiSocketdotio,
  SiTailwindcss,
  SiVercel,
} from 'react-icons/si';
import { TbApi, TbBrandReactNative, TbBrandVscode } from 'react-icons/tb';

import Reveal, { SectionTitle } from './Reveal';

const GROUPS = [
  {
    key: 'frontend',
    label: 'Frontend',
    items: [
      { icon: <FaJs />, name: 'JavaScript' },
      { icon: <FaReact />, name: 'React.js' },
      { icon: <SiNextdotjs />, name: 'Next.js' },
      { icon: <SiRedux />, name: 'Redux Toolkit' },
      { icon: <SiTailwindcss />, name: 'Tailwind CSS' },
      { icon: <FaHtml5 />, name: 'HTML5' },
      { icon: <FaCss3Alt />, name: 'CSS3' },
      { icon: <FaBootstrap />, name: 'Bootstrap' },
    ],
  },
  {
    key: 'mobile',
    label: 'Mobile',
    items: [
      { icon: <TbBrandReactNative />, name: 'React Native' },
      { icon: <SiExpo />, name: 'Expo' },
      { icon: <SiExpo />, name: 'Expo Router' },
      { icon: <FaReact />, name: 'Reanimated' },
      { icon: <FaMapMarkedAlt />, name: 'MapLibre GL' },
      { icon: <SiExpo />, name: 'EAS Build / Update' },
    ],
  },
  {
    key: 'backend',
    label: 'Backend & DB',
    items: [
      { icon: <FaNode />, name: 'Node.js' },
      { icon: <SiExpress />, name: 'Express.js' },
      { icon: <TbApi />, name: 'REST APIs' },
      { icon: <SiSocketdotio />, name: 'Socket.io' },
      { icon: <SiJsonwebtokens />, name: 'JWT Auth' },
      { icon: <SiMongodb />, name: 'MongoDB' },
      { icon: <SiMysql />, name: 'SQL' },
    ],
  },
  {
    key: 'languages',
    label: 'Languages & CS',
    items: [
      { icon: <FaJs />, name: 'JavaScript' },
      { icon: <FaJava />, name: 'Java' },
      { icon: <FaDatabase />, name: 'SQL' },
      { icon: <FaPython />, name: 'Python' },
      { icon: <FaBrain />, name: 'DSA' },
      { icon: <FaBrain />, name: 'OOP' },
      { icon: <FaDatabase />, name: 'DBMS' },
      { icon: <FaRobot />, name: 'Operating Systems' },
    ],
  },
  {
    key: 'tools',
    label: 'Tools & AI',
    items: [
      { icon: <FaGitAlt />, name: 'Git' },
      { icon: <FaGithub />, name: 'GitHub' },
      { icon: <SiPostman />, name: 'Postman' },
      { icon: <TbBrandVscode />, name: 'VS Code' },
      { icon: <SiVercel />, name: 'Vercel' },
      { icon: <SiFlask />, name: 'Flask' },
      { icon: <FaBrain />, name: 'LangChain / RAG' },
      { icon: <FaRobot />, name: 'Hugging Face' },
    ],
  },
];

export default function NewSkills() {
  const [active, setActive] = React.useState(GROUPS[0].key);
  const group = GROUPS.find((g) => g.key === active) || GROUPS[0];

  return (
    <section className="section" id="skills">
      <SectionTitle
        eyebrow="What I work with"
        title="Skills & Technologies"
        subtitle="The stack I use day to day across mobile, web, and backend."
      />

      <Reveal>
        <div className="skill-tabs" role="tablist">
          {GROUPS.map((g) => (
            <button
              key={g.key}
              type="button"
              role="tab"
              aria-selected={active === g.key}
              className={`skill-tab${active === g.key ? ' active' : ''}`}
              onClick={() => setActive(g.key)}
            >
              {g.label}
            </button>
          ))}
        </div>
      </Reveal>

      <AnimatePresence mode="wait">
        <motion.div
          key={group.key}
          className="skill-grid"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
        >
          {group.items.map((s, i) => (
            <motion.div
              key={`${group.key}-${s.name}`}
              className="skill-item"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.34, delay: i * 0.035, ease: [0.22, 1, 0.36, 1] }}
            >
              {s.icon}
              <span>{s.name}</span>
            </motion.div>
          ))}
        </motion.div>
      </AnimatePresence>
    </section>
  );
}
